import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lesson, TourStep, lessonById } from './tours';
import { TOUR_EVENT, lessonAction } from './tourEvents';

// =============================================================================
// Guided lessons: spotlights the real element a step is about, explains it,
// and moves on when the user actually does the thing (or presses Next).
// The running lesson survives page reloads (sessionStorage) because some
// flows — e.g. Search → new project — end in a full navigation.
// =============================================================================

const RUN_KEY = 'mm.tour.run';
const DONE_KEY = 'mm.tour.done';
const PAD = 6;              // spotlight padding around the element
const CARD_W = 340;
// Room left for an open dropdown when a 'side' card has to sit on the left.
const SIDE_GAP = 260;
const OPTIONAL_AFTER_MS = 4000;   // optional targets: don't keep people waiting
const SKIP_AFTER_MS = 2500;      // skipIfMissing steps: the screen has had time to load
const MISSING_AFTER_MS = 30000; // generous: big projects take a while to load the map

interface RunState {
  lessonId: string;
  step: number;
  /** The project the lesson is on ('/project/123'), fixed the first time a
   *  project step is reached, so the cards never follow the user into a
   *  different project. */
  projectPath?: string;
  /** Furthest step reached — steps before it are being revisited (Back), so
   *  they show Next instead of waiting for an action already done. */
  maxStep?: number;
  /** Last change (ms). A lesson untouched for RUN_STALE_MS is dropped, so
   *  one left behind (a crash, a closed laptop) doesn't pop up later. */
  at?: number;
}

const RUN_STALE_MS = 2 * 60 * 60 * 1000;

// A step's route is a path prefix, optionally with a query that must match
// too ('/settings?tab=integrations' shows only on that tab).
const routeMatches = (route: string, pathname: string, search: string) => {
  const [path, query] = route.split('?');
  if (!pathname.startsWith(path)) return false;
  if (!query) return true;
  const have = new URLSearchParams(search);
  return Array.from(new URLSearchParams(query).entries()).every(([k, v]) => have.get(k) === v);
};

// If a lesson card ever throws, end the lesson instead of blanking the app.
class OverlayBoundary extends React.Component<{ onError: () => void; children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(e: unknown) {
    // eslint-disable-next-line no-console
    console.error('[tour] lesson card failed, lesson ended:', e);
    this.props.onError();
  }
  render() { return this.state.failed ? null : this.props.children; }
}

interface TourContextValue {
  startLesson: (id: string) => void;
  completed: string[];
  running: string | null;
  /** id of the step on screen, so screens can react to it */
  stepId: string | null;
}

const TourContext = createContext<TourContextValue>({ startLesson: () => {}, completed: [], running: null, stepId: null });
export const useTour = () => useContext(TourContext);

const readJSON = <T,>(store: Storage | null, key: string, fallback: T): T => {
  try { const v = store?.getItem(key); return v ? JSON.parse(v) as T : fallback; } catch { return fallback; }
};
const writeJSON = (store: Storage | null, key: string, value: unknown) => {
  try { if (value === null) store?.removeItem(key); else store?.setItem(key, JSON.stringify(value)); } catch { /* storage blocked */ }
};
const session = (): Storage | null => { try { return window.sessionStorage; } catch { return null; } };
const local = (): Storage | null => { try { return window.localStorage; } catch { return null; } };

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [run, setRun] = useState<RunState | null>(() => {
    const r = readJSON<RunState | null>(session(), RUN_KEY, null);
    return r && (!r.at || Date.now() - r.at > RUN_STALE_MS) ? null : r;
  });
  const [completed, setCompleted] = useState<string[]>(() => readJSON<string[]>(local(), DONE_KEY, []));

  const lesson = run ? lessonById(run.lessonId) : null;
  const step: TourStep | null = lesson && run ? lesson.steps[run.step] || null : null;

  const save = useCallback((next: RunState | null) => {
    const stamped = next ? { ...next, at: Date.now() } : null;
    setRun(stamped);
    writeJSON(session(), RUN_KEY, stamped);
  }, []);

  const startLesson = useCallback(async (id: string) => {
    const l = lessonById(id);
    if (!l) return;
    let path = l.startPath;
    if (l.anyProject) {
      // The sample project if there is one; made for the user if they have
      // Search; otherwise their most recent project — these steps work on any.
      const token = window.localStorage.getItem('token');
      const auth = { Authorization: `Bearer ${token}` };
      let projectId: number | null = null;
      try {
        const r = await fetch('/api/universe/lesson/sample-project', { headers: auth });
        if (r.ok) projectId = (await r.json()).projectId;
        if (!projectId) {
          const c = await fetch('/api/universe/lesson/search-to-map/project', { method: 'POST', headers: auth });
          if (c.ok) projectId = (await c.json()).projectId || null;
        }
        if (!projectId) {
          const p = await fetch('/api/projects', { headers: auth });
          if (p.ok) {
            const list = await p.json();
            const arr = Array.isArray(list) ? list : (list.projects || []);
            if (arr.length) projectId = [...arr].sort((a: any, b: any) => String(b.created_at).localeCompare(String(a.created_at)))[0].id;
          }
        }
      } catch { /* fall through */ }
      if (!projectId) {
        window.alert('This lesson runs on a project — create one first (Search or Upload), then start it again.');
        return;
      }
      path = `/project/${projectId}`;
    } else if (l.startOnSampleProject) {
      try {
        const token = window.localStorage.getItem('token');
        const resp = await fetch('/api/universe/lesson/sample-project', { headers: { Authorization: `Bearer ${token}` } });
        if (!resp.ok) {
          window.alert('This lesson uses the sample project from "Build your first search" — do that lesson first.');
          return;
        }
        path = `/project/${(await resp.json()).projectId}`;
      } catch {
        window.alert('Could not open the sample project — please try again.');
        return;
      }
    }
    save({ lessonId: id, step: 0 });
    navigate(path);
  }, [navigate, save]);

  const advance = useCallback(() => {
    if (!run || !lesson) return;
    const nextIndex = run.step + 1;
    if (nextIndex < lesson.steps.length) {
      save({ ...run, step: nextIndex, maxStep: Math.max(run.maxStep ?? run.step, nextIndex) });
      return;
    }
    save(null);
    const done = Array.from(new Set([...completed, lesson.id]));
    setCompleted(done);
    writeJSON(local(), DONE_KEY, done);
  }, [run, lesson, completed, save]);

  const stop = useCallback(() => save(null), [save]);
  const back = useCallback(() => {
    if (!run || run.step === 0) return;
    save({ ...run, step: run.step - 1, maxStep: Math.max(run.maxStep ?? run.step, run.step) });
  }, [run, save]);
  const revisited = !!run && run.step < (run.maxStep ?? run.step);

  // Steps waiting on the app: screens call tourEvent(name). A revisited
  // step (after Back) is already done — it waits for Next instead, so the
  // user can actually re-read it.
  useEffect(() => {
    if (!step || step.advance.kind !== 'event' || revisited) return;
    const want = step.advance.name;
    const onEvent = (e: Event) => { if ((e as CustomEvent).detail === want) advance(); };
    window.addEventListener(TOUR_EVENT, onEvent);
    return () => window.removeEventListener(TOUR_EVENT, onEvent);
  }, [step, advance, revisited]);

  // Keys: Esc leaves; ← goes back; → moves on where the step isn't waiting.
  useEffect(() => {
    if (!run || !step) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'Escape') stop();
      else if (e.key === 'ArrowLeft') back();
      else if (e.key === 'ArrowRight' && (step.advance.kind === 'next' || revisited)) advance();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [run, step, stop, back, advance, revisited]);

  // Pin the lesson to the first project it lands on.
  useEffect(() => {
    if (!run || !step || run.projectPath || step.route !== '/project/') return;
    const m = location.pathname.match(/^\/project\/\d+/);
    if (m) save({ ...run, projectPath: m[0] });
  }, [run, step, location.pathname, save]);

  const onRoute = !!step && (
    step.route === '/project/' && run?.projectPath
      ? location.pathname.startsWith(run.projectPath)
      : routeMatches(step.route, location.pathname, location.search)
  );
  // Where "Resume" takes the user when they've wandered off the lesson's screen.
  const resumePath = step
    ? (step.route === '/project/' ? (run?.projectPath || '/projects')
      : step.route)
    : '/training';

  return (
    <TourContext.Provider value={{ startLesson, completed, running: run?.lessonId || null, stepId: step?.id || null }}>
      {children}
      {lesson && step && run && onRoute && (
        <OverlayBoundary key={`b:${lesson.id}:${step.id}`} onError={stop}>
        <StepOverlay
          key={`${lesson.id}:${step.id}`}
          lesson={lesson}
          step={step}
          index={run.step}
          revisited={revisited}
          onNext={advance}
          onBack={back}
          onStop={stop}
          onLearn={() => { advance(); navigate('/training'); }}
        />
        </OverlayBoundary>
      )}
      {lesson && step && run && !onRoute && (
        <div
          data-tour-ui=""
          className="fixed z-[10000] bottom-5 right-5 bg-white border border-gray-200 shadow-xl rounded-full pl-4 pr-2 py-1.5 flex items-center gap-3 text-sm"
          role="status"
        >
          <span className="text-gray-600">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-teal-700 mr-2">Lesson paused</span>
            {lesson.title} · step {run.step + 1} of {lesson.steps.length}
          </span>
          <button onClick={() => navigate(resumePath)} className="px-3 py-1 bg-teal-600 text-white rounded-full text-xs font-medium hover:bg-teal-700">Resume</button>
          <button onClick={stop} className="px-2 py-1 text-xs text-gray-400 hover:text-gray-700" aria-label="Leave lesson">Leave</button>
        </div>
      )}
    </TourContext.Provider>
  );
};

// ---------------------------------------------------------------------------

interface Rect { top: number; left: number; width: number; height: number }

const sameRect = (a: Rect | null, b: Rect | null) =>
  !!a && !!b && Math.abs(a.top - b.top) < 1 && Math.abs(a.left - b.left) < 1
  && Math.abs(a.width - b.width) < 1 && Math.abs(a.height - b.height) < 1;

const StepOverlay: React.FC<{
  lesson: Lesson; step: TourStep; index: number; revisited: boolean;
  onNext: () => void; onBack: () => void; onStop: () => void; onLearn: () => void;
}> = ({ lesson, step, index, revisited, onNext, onBack, onStop, onLearn }) => {
  const [rect, setRect] = useState<Rect | null>(null);
  const [missing, setMissing] = useState(false);
  const scrolled = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardH, setCardH] = useState(180);

  const targets = step.target ? (Array.isArray(step.target) ? step.target : [step.target]) : [];
  const targetKey = targets.join(',');

  // Track the target's position (it moves with scrolling, resizing, loading).
  // Several targets are lit as one area (their bounding box).
  useEffect(() => {
    if (!targetKey) return;
    const names = targetKey.split(',');
    const started = Date.now();
    let prev: Rect | null = null;
    let warned = false;
    const tick = () => {
      const rects = names
        .map((n) => document.querySelector<HTMLElement>(`[data-tour="${n}"]`)?.getBoundingClientRect())
        .filter((x): x is DOMRect => !!x && (x.width > 0 || x.height > 0));
      const el = rects.length === names.length ? document.querySelector<HTMLElement>(`[data-tour="${names[0]}"]`) : null;
      const r = el ? {
        top: Math.min(...rects.map((x) => x.top)),
        left: Math.min(...rects.map((x) => x.left)),
        bottom: Math.max(...rects.map((x) => x.bottom)),
        right: Math.max(...rects.map((x) => x.right)),
      } : null;
      if (!el || !r) {
        // Keep the card where it is while the target is briefly gone (a
        // popover closing, a list re-rendering): unmounting it at once
        // swallows the click that was in progress on Next.
        if (Date.now() - started > (step.optional ? OPTIONAL_AFTER_MS : MISSING_AFTER_MS) && !warned) {
          warned = true;
          setMissing(true);
          prev = null;
          setRect(null);
          // eslint-disable-next-line no-console
          if (!step.optional) console.warn(`[tour] "${lesson.id}" step "${step.id}": no element with data-tour="${targetKey}" on ${window.location.pathname}`);
        }
        return;
      }
      setMissing(false);
      if (!scrolled.current) {
        scrolled.current = true;
        // Also scrolls any panel the target sits in (the filter list): a
        // target clipped inside a scrolling panel still reports a position,
        // and the spotlight would land on whatever is drawn there instead.
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      const next = { top: r.top, left: r.left, width: r.right - r.left, height: r.bottom - r.top };
      if (!sameRect(prev, next)) { prev = next; setRect(next); }
    };
    tick();
    const id = window.setInterval(tick, 150);
    return () => window.clearInterval(id);
  }, [targetKey, step.id, step.optional, lesson.id]);

  // "Click the highlighted thing" steps.
  useEffect(() => {
    if (step.advance.kind !== 'click' || !step.target || !rect) return;
    const el = document.querySelector<HTMLElement>(`[data-tour="${targets[0]}"]`);
    if (!el) return;
    // Advance immediately (capture phase): some clicks load a new page, and
    // the next step must be saved before that happens.
    const onClick = () => onNext();
    el.addEventListener('click', onClick, true);
    return () => el.removeEventListener('click', onClick, true);
  }, [step, rect, onNext]); // eslint-disable-line react-hooks/exhaustive-deps

  // Measure the card so it can sit above the target when there's no room below.
  useLayoutEffect(() => {
    if (cardRef.current) setCardH(cardRef.current.offsetHeight);
  }, [step.id, rect, missing]);

  // (Hooks first: everything below may return early.)
  // A step that only applies to some people (e.g. Starter's "Start my
  // month" card): when its target isn't on screen, pass it by in the
  // direction the user was going, without showing the card.
  const skipped = useRef(false);
  useEffect(() => {
    if (!step.skipIfMissing || rect || skipped.current) return;
    const t = window.setTimeout(() => {
      if (document.querySelector(`[data-tour="${targets[0]}"]`)) return;
      skipped.current = true;
      if (revisited && index > 0) onBack(); else onNext();
    }, SKIP_AFTER_MS);
    return () => window.clearTimeout(t);
  }, [step.id, step.skipIfMissing, rect]); // eslint-disable-line react-hooks/exhaustive-deps
  if (step.skipIfMissing && !rect) return null;

  const spot = rect && step.target ? {
    top: Math.max(0, rect.top - PAD),
    left: Math.max(0, rect.left - PAD),
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
  } : null;

  // Card placement: below, else above, else beside, else bottom-right.
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let cardStyle: React.CSSProperties;
  const sideTop = spot ? Math.min(Math.max(16, spot.top), vh - cardH - 16) : 0;
  if (step.placement === 'center' && (spot || !step.target)) {
    cardStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  } else if (spot && step.placement === 'side' && spot.left + spot.width + 12 + CARD_W < vw - 16) {
    // Column ⋮ menus open below the button and extend to the left, so the
    // right-hand side stays clear of them.
    cardStyle = { top: sideTop, left: spot.left + spot.width + 12 };
  } else if (spot && step.placement === 'side' && spot.left - SIDE_GAP - CARD_W > 16) {
    cardStyle = { top: sideTop, left: spot.left - SIDE_GAP - CARD_W };
  } else if (spot) {
    const left = Math.min(Math.max(16, spot.left), vw - CARD_W - 16);
    if (spot.top + spot.height + 12 + cardH < vh - 16) {
      cardStyle = { top: spot.top + spot.height + 12, left };
    } else if (spot.top - 12 - cardH > 16) {
      cardStyle = { top: spot.top - 12 - cardH, left };
    } else if (spot.left + spot.width + 12 + CARD_W < vw - 16) {
      cardStyle = { top: Math.min(Math.max(16, spot.top), vh - cardH - 16), left: spot.left + spot.width + 12 };
    } else if (spot.left - 12 - CARD_W > 16) {
      cardStyle = { top: Math.min(Math.max(16, spot.top), vh - cardH - 16), left: spot.left - 12 - CARD_W };
    } else {
      cardStyle = { bottom: 24, right: 24 };
    }
  } else if (step.target && !missing) {
    return null; // still waiting for the element to render
  } else if (!step.target) {
    cardStyle = { bottom: 24, right: 24 };
  } else {
    cardStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  const waiting = step.advance.kind !== 'next' && !revisited;
  const last = index === lesson.steps.length - 1;
  const dim = 'rgba(15, 23, 42, 0.55)';


  return (
    <>
      {spot && (
        <>
          {/* Four panels dim everything except the spotlight and block stray clicks;
              the element itself stays fully usable. */}
          <div className="fixed z-[9998]" style={{ top: 0, left: 0, right: 0, height: spot.top, background: dim }} />
          <div className="fixed z-[9998]" style={{ top: spot.top + spot.height, left: 0, right: 0, bottom: 0, background: dim }} />
          <div className="fixed z-[9998]" style={{ top: spot.top, left: 0, width: spot.left, height: spot.height, background: dim }} />
          <div className="fixed z-[9998]" style={{ top: spot.top, left: spot.left + spot.width, right: 0, height: spot.height, background: dim }} />
          <div
            className="fixed z-[9998] pointer-events-none rounded-lg ring-2 ring-teal-400 ring-offset-0 transition-all duration-200"
            style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height, boxShadow: '0 0 0 4px rgba(45, 212, 191, 0.25)' }}
          />
        </>
      )}
      <div
        ref={cardRef}
        data-tour-ui=""
        role="dialog"
        aria-label={step.title}
        className="fixed z-[10000] bg-white rounded-xl shadow-2xl border border-gray-200 p-4"
        style={{ width: CARD_W, maxWidth: 'calc(100vw - 32px)', ...cardStyle }}
      >
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-teal-700">
            {lesson.title} · {index + 1} of {lesson.steps.length}
          </span>
          <button onClick={onStop} className="text-gray-400 hover:text-gray-700 text-lg leading-none" aria-label="Leave lesson">×</button>
        </div>
        <div className="h-1 bg-gray-100 rounded-full mb-3 overflow-hidden">
          <div className="h-full bg-teal-500 rounded-full transition-all duration-300" style={{ width: `${((index + 1) / lesson.steps.length) * 100}%` }} />
        </div>
        <h3 className="text-[15px] font-semibold text-gray-900 mb-1" style={{ textWrap: 'balance' } as React.CSSProperties}>{step.title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed">{step.body}</p>
        {missing && !step.optional && (
          <p className="text-xs text-amber-700 mt-2">
            We can’t find this part of the screen right now — it may be hidden. You can move on, or leave the lesson and come back.
          </p>
        )}
        {waiting && !missing && step.hint && (
          <p className="text-xs text-teal-700 mt-3 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
            {step.hint}
          </p>
        )}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-3">
            {index > 0 && (
              <button onClick={onBack} className="text-xs text-gray-500 hover:text-gray-800" title="Previous step (← key)">‹ Back</button>
            )}
            {last
              ? <button onClick={onLearn} className="text-sm text-teal-700 hover:text-teal-900">Back to Learn</button>
              : <button onClick={onStop} className="text-xs text-gray-400 hover:text-gray-700">Leave lesson</button>}
          </div>
          <div className="flex items-center gap-2">
            {/* "Do it for me": on waiting steps it's the main button; on Next
                steps it sits beside Next (e.g. fill an example, then Next). */}
            {step.action && !missing && (
              <button onClick={() => lessonAction(step.action!.name)}
                className={waiting
                  ? 'px-3.5 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700'
                  : 'px-3 py-1.5 text-teal-700 border border-teal-200 rounded-lg text-sm font-medium hover:bg-teal-50'}>
                {step.action.label}
              </button>
            )}
            {(!waiting || missing) && (
              <button onClick={onNext} className="px-3.5 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">
                {last ? 'Finish' : 'Next'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
